import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to fetch bubbles data from CoinGecko (via backend)
 * @param {string} mode - 'sectors' or 'top100'
 * @param {number} refreshInterval - Refresh interval in ms (default 60s)
 */
export function useBubblesData(mode = 'sectors', refreshInterval = 60000) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = useCallback(async () => {
    // Retry logic to handle CDN cache issues (HTML instead of JSON)
    const fetchWithRetry = async (retries = 3) => {
      for (let i = 0; i < retries; i++) {
        try {
          // Add cache-busting parameter to bypass Cloudflare cache
          const response = await fetch(`${API_BASE_URL}/api/bubbles?mode=${mode}&_t=${Date.now()}`);

          if (!response.ok) {
            if (i < retries - 1) {
              await new Promise(r => setTimeout(r, 1000 * (i + 1)));
              continue;
            }
            throw new Error(`Failed to fetch bubbles data: ${response.status}`);
          }

          const text = await response.text();
          // Check if response is HTML instead of JSON (CDN cache issue)
          if (text.startsWith('<!DOCTYPE') || text.startsWith('<html')) {
            console.warn(`[Bubbles] Received HTML instead of JSON (attempt ${i + 1}/${retries})`);
            if (i < retries - 1) {
              await new Promise(r => setTimeout(r, 1000 * (i + 1)));
              continue;
            }
            throw new Error('Received HTML instead of JSON');
          }

          return JSON.parse(text);
        } catch (err) {
          if (i === retries - 1) throw err;
          await new Promise(r => setTimeout(r, 1000 * (i + 1)));
        }
      }
    };

    try {
      const result = await fetchWithRetry();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch bubbles data');
      }

      setData(result.data);
      setLastUpdate(result.lastUpdate);
      setError(null);
    } catch (err) {
      console.error('[Bubbles] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    // Reset loading state when mode changes
    setLoading(true);

    // Fetch immediately
    fetchData();

    // Set up refresh interval
    const interval = setInterval(fetchData, refreshInterval);

    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  return {
    data,
    loading,
    error,
    lastUpdate,
    refresh: fetchData,
  };
}

export default useBubblesData;
